import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { setTokens } from "@/lib/native";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AccountSettings() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [optOut, setOptOut] = useState(!!user?.email_opt_out);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleEmails = async (checked) => {
    setOptOut(!checked);
    try {
      await api.patch("/me/email-preferences", { email_opt_out: !checked });
      setUser((u) => (u ? { ...u, email_opt_out: !checked } : u));
      toast.success(checked ? "Reminders and updates switched on" : "You won't receive reminders or updates");
    } catch { setOptOut(checked); toast.error("Could not save preference"); }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/me/delete", { password });
      setTokens(null);
      setUser(false);
      toast.success("Your account has been deleted");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete account");
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-border bg-card shadow-sm p-6 space-y-6" data-testid="account-settings">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-semibold inline-flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Reminder & update emails</p>
          <p className="text-sm text-muted-foreground mt-1">Inspection countdowns, weekly regulatory digests and renewal reminders. Receipts and account emails are always sent.</p>
        </div>
        <Switch checked={!optOut} onCheckedChange={toggleEmails} data-testid="email-prefs-toggle" />
      </div>
      {user?.role !== "admin" && (
        <div className="border-t border-border pt-5 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold inline-flex items-center gap-2"><Trash2 className="w-4 h-4 text-destructive" /> Delete my account</p>
            <p className="text-sm text-muted-foreground mt-1">Closes your account, cancels any plan immediately and erases your conversations, records and personal data. Payment records are kept for 6 years as required by HMRC.</p>
          </div>
          <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => setOpen(true)} data-testid="delete-account-button">Delete account</Button>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" /> Delete your account?</DialogTitle>
            <DialogDescription>This cannot be undone. Enter your password to confirm.</DialogDescription>
          </DialogHeader>
          <form onSubmit={deleteAccount} className="space-y-3">
            <div><Label htmlFor="del-pw">Password</Label>
              <Input id="del-pw" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="delete-account-password" /></div>
            <Button type="submit" variant="destructive" className="w-full" disabled={busy || !password} data-testid="delete-account-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Permanently delete my account"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
